import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, CAD, stageClass, STAGES, REASON_STAGES, ACCEPT_STAGES, fmtDate, DOC_STATUSES, PRIORITIES, DECISIONS, ENROLL_STATUSES, COMMISSION_STATUSES, priorityClass } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Upload, Download, FileText, Trash2, ChevronLeft, UserCircle2, Building2, Plus, School, BookOpen, CalendarDays, Lock, Layers, CheckCircle2, ChevronDown } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import ExpiryDocCard from "../components/ExpiryDocCard";
import MissingDocsPicker from "../components/MissingDocsPicker";
import FilingStatusModal from "../components/FilingStatusModal";
import { upcomingIntakes } from "../lib/intakes";
import AppNotesPanel from "../components/AppNotesPanel";
import CategoryCourseFields from "../components/CategoryCourseFields";
import { StageReasonModal, SiblingDecisionModal } from "../components/StageDecisionModals";

export default function ApplicationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ users: [], partners: [] });
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [filingModal, setFilingModal] = useState({ open: false, status: "" });

  const canFinance = ["admin","manager","management","bdm"].includes(user?.role);
  const [reasonStage, setReasonStage] = useState(null);
  const [siblingAsk, setSiblingAsk] = useState(null);

  const load = async () => {
    const [d, m] = await Promise.all([api.get(`/applications/${id}`), api.get("/meta")]);
    setData(d.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const a = data.application;

  const patch = async (body) => {
    try {
      const r = await api.patch(`/applications/${id}`, body);
      toast.success(r.data?.backed_out ? `Updated · ${r.data.backed_out} other application(s) closed` : "Updated");
      load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const openSiblings = (data.siblings || []).filter(s => !s.deposit_paid && !ACCEPT_STAGES.includes(s.stage)
    && ![...REASON_STAGES, "Application Rejected", "Closed", "Deferred"].includes(s.stage));
  const alreadyAccepted = a.deposit_paid || ACCEPT_STAGES.includes(a.stage);
  // Resolves {sibling_action} or null when cancelled; skips the question when nothing is open.
  const askSiblings = () => REASON_STAGES.includes(a.stage)
    ? (toast.error(`This application is ${a.stage}. Reopen it before marking it accepted.`), Promise.resolve(null))
    : (alreadyAccepted || !openSiblings.length)
    ? Promise.resolve({})
    : new Promise(resolve => setSiblingAsk({ resolve }));

  const changeStage = async (v) => {
    if (v === a.stage) return;
    if (REASON_STAGES.includes(v)) return setReasonStage(v);
    const extra = ACCEPT_STAGES.includes(v) ? await askSiblings() : {};
    if (extra === null) return;
    patch({ stage: v, ...extra });
  };

  const uploadFile = async (docType, file = null) => {
    const f = file || fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/applications/${id}/upload?doc_type=${docType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); if (fileRef.current) fileRef.current.value = ""; load();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };
  const pickAndUpload = (docType) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = ".pdf,.jpg,.jpeg,.png";
    inp.onchange = (e) => { const f = e.target.files?.[0]; if (f) uploadFile(docType, f); };
    inp.click();
  };

  const downloadFile = async (f) => {
    const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const link = document.createElement("a");
    link.href = url; link.download = f.original_filename; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3 app-compact">
      <Link to="/applications" data-testid="back-apps" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ChevronLeft className="w-4 h-4" /> Back to applications
      </Link>

      <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center heading-font text-base font-bold">
              {a.student_name?.split(" ").map(s=>s[0]).slice(0,2).join("")}
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <Link to={data.student ? `/students/${data.student.id}` : "#"} data-testid="app-student-name-link" title="Open student profile"
                  className="heading-font text-xl font-bold text-slate-900 hover:text-orange-600 hover:underline underline-offset-4 decoration-2 transition-colors">{a.student_name}</Link>
                <span className="mono text-xs text-slate-500">{a.application_id}</span>
                <span className="text-xs text-slate-500">{[a.email, a.phone].filter(Boolean).join(" · ")}</span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <span className={`stage-badge ${stageClass(a.stage)}`}>{a.stage}</span>
                {data.student && (
                  <Link data-testid="app-student-link" to={`/students/${data.student.id}`} className="stage-badge bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100">
                    <UserCircle2 className="w-3 h-3" /> {data.student.student_code}
                  </Link>
                )}
                {data.partner && (
                  <Link to={`/partners/${data.partner.id}`} className="stage-badge bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100">
                    <Building2 className="w-3 h-3" /> {data.partner.company_name}
                  </Link>
                )}
                <SiblingOffersChip siblings={data.siblings || []} />
              </div>
              {REASON_STAGES.includes(a.stage) && (
                <div data-testid="closure-reason" className="mt-1.5 text-xs text-zinc-600 truncate" title={a.closure_reason || a.backed_out_reason}>
                  <b>{a.stage}</b> — {a.closure_reason || a.backed_out_reason || "No reason recorded"}{a.closure_reason_by && ` · by ${a.closure_reason_by}`}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Offer Status</Label>
              <Select value={a.stage} onValueChange={changeStage}>
                <SelectTrigger data-testid="stage-select" className="h-9 w-[220px]"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Filing Status</Label>
              <Select
                value={a.decision || ""}
                onValueChange={v => { if (v !== a.decision) setFilingModal({ open: true, status: v }); }}
                disabled={a.stage !== "Admission Complete"}
              >
                <SelectTrigger
                  data-testid="decision-select"
                  className="h-9 w-[160px] disabled:opacity-60 disabled:cursor-not-allowed"
                  title={a.stage !== "Admission Complete" ? "Available after Offer Status reaches 'Admission Complete'" : ""}
                >
                  <SelectValue placeholder={a.stage !== "Admission Complete" ? "Locked — not admitted" : "Select status"} />
                </SelectTrigger>
                <SelectContent>{DECISIONS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-3 items-start">
      <Tabs defaultValue="overview" className="min-w-0">
        <TabsList className="bg-white border border-slate-200 h-auto min-h-9 flex-wrap justify-start">
          {["overview","admissions","documents","enrollment",...(canFinance ? ["commissions"] : []),"tasks","activity"].map(t => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          {/* Side-by-side expiry cards — same color-coding as B2C ExpiryDocCard
              (red <14d, amber <30d, green >30d, grey when unset).
              LEFT: Study-permit deadline + permit upload.
              RIGHT: Fee-payment deadline + receipt upload — helps BDM chase
              the partner for the deposit before it lapses. */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <ExpiryDocCard
              applicationId={id}
              uploadPath={`/applications/${id}/upload`}
              downloadPath={`/files`}
              docType="Study Permit"
              dateField="study_permit_deadline"
              dateLabel="Permit Expiry"
              expiryLabel="Permit Expiry"
              variant="fun"
              highlights={[
                { key: "college", icon: School, label: a.college, value: a.college_code || a.college || "No college", className: "bg-indigo-600 text-white mono" },
                { key: "program", icon: BookOpen, label: a.program_category || "Program", value: a.course_name || a.program || "No course", grow: true, className: "bg-white text-slate-800 border border-slate-200" },
                { key: "intake", icon: CalendarDays, label: "Intake", value: a.intake || "No intake", className: "bg-orange-100 text-orange-800 border border-orange-200" },
              ]}
              initialDate={a.study_permit_deadline}
              initialFileId={a.expiry_doc_file_id}
              initialFileName={a.expiry_doc_file_name}
              completionField="visa_applied"
              completeLockedReason={a.deposit_paid ? null : "Mark Deposit Paid first — visa is applied after the deposit"}
              completionDateField="visa_applied_date"
              completionLabel="Visa Applied"
              completionDoneMessage="Visa Applied"
              completionSubMessage="Study permit applied on time"
              initialComplete={a.visa_applied}
              initialCompleteDate={a.visa_applied_date}
              onSaved={()=>load()}
            />
            <ExpiryDocCard
              applicationId={id}
              uploadPath={`/applications/${id}/upload`}
              downloadPath={`/files`}
              docType="Fee Receipt"
              dateField="payment_deadline"
              dateLabel="Pay By"
              expiryLabel="Fee Payment Deadline"
              fileIdField="payment_doc_file_id"
              fileNameField="payment_doc_file_name"
              showDocument={false}
              variant="fun"
              highlights={[
                { key: "partner", icon: Building2, label: "Partner", value: data.partner?.company_name || "No partner", grow: true, className: "bg-white text-slate-800 border border-slate-200" },
                { key: "fee-college", icon: School, label: a.college, value: a.college_code || a.college || "No college", className: "bg-indigo-600 text-white mono" },
              ]}
              completionSubMessage="Deposit receipt on file"
              initialDate={a.payment_deadline}
              initialFileId={a.payment_doc_file_id}
              initialFileName={a.payment_doc_file_name}
              completionField="deposit_paid"
              undoLockedReason={a.visa_applied ? "Undo Visa Applied first — the visa can't be applied without a paid deposit" : null}
              beforeComplete={askSiblings}
              completionDateField="deposit_paid_date"
              completionLabel="Deposit Paid"
              completionDoneMessage="Deposit Paid"
              completionRequiresFile={true}
              completionFilePrompt="Upload the fees payment receipt or screenshot to confirm the deposit was paid."
              initialComplete={a.deposit_paid}
              initialCompleteDate={a.deposit_paid_date}
              onSaved={()=>load()}
            />
          </div>
          {/* Editable overview — officer can tweak core student + application
              fields in-place. Read-only timestamps (Date Received, Offer Received,
              Fees Paid, PAL Issued) stay as Info tiles since they're system-stamped. */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-3 gap-y-2.5">
            <Field label="Date of Birth">
              <Input data-testid="ov-dob" type="date" defaultValue={a.date_of_birth||""} onBlur={e=>e.target.value!==(a.date_of_birth||"") && patch({date_of_birth:e.target.value})}/>
            </Field>
            <Field label="Student Location">
              <Select value={a.student_location||""} onValueChange={v=>patch({student_location:v})}>
                <SelectTrigger data-testid="ov-student-location"><SelectValue placeholder="Choose"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Onshore">Onshore</SelectItem>
                  <SelectItem value="Offshore">Offshore</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Documents Status">
              <Select value={a.documents_status} onValueChange={v=>patch({documents_status:v})}>
                <SelectTrigger data-testid="ov-docs-status"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="College">
              {a.college_id ? <LockedCollege a={a} testid="ov-college" /> : (
              <Select value={a.college_id||""} onValueChange={v=>patch({college_id: v})}>
                <SelectTrigger data-testid="ov-college"><SelectValue placeholder="Select college" /></SelectTrigger>
                <SelectContent>
                  {(meta.colleges||[]).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} — ${c.name}` : c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>)}
            </Field>
            <CategoryCourseFields a={a} patch={patch} prefix="ov" />
            <Field label="Intake">
              <Select value={a.intake_id||""} onValueChange={v=>patch({intake_id: v})}>
                <SelectTrigger data-testid="ov-intake"><SelectValue placeholder="Select intake" /></SelectTrigger>
                <SelectContent>
                  {upcomingIntakes(meta.intakes, a.intake_id).map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="College App ID">
              <Input data-testid="ov-college-app-id" defaultValue={a.college_app_id||""} onBlur={e=>e.target.value!==(a.college_app_id||"") && patch({college_app_id:e.target.value})}/>
            </Field>
            <Field label="College Student ID">
              <Input data-testid="ov-college-student-id" defaultValue={a.college_student_id||""} placeholder="e.g. N12345678" onBlur={e=>e.target.value!==(a.college_student_id||"") && patch({college_student_id:e.target.value})}/>
            </Field>
            <div data-testid="ov-key-dates" className="sm:col-span-2 xl:col-span-3 flex flex-wrap gap-1.5 pt-1">
              {[["Received", a.date_received], ["Offer", a.offer_received_date], ["Fees Paid", a.fees_paid_date], ["PAL", a.pal_issued_date]].map(([l, v]) => (
                <span key={l} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs">
                  <span className="uppercase tracking-wide text-[10px] text-slate-500">{l}</span>
                  <span className="font-medium text-slate-800">{v ? fmtDate(v) : "—"}</span>
                </span>
              ))}
            </div>
          </div>
          {a.stage === "Additional Documents Required" && (
            <div data-testid="additional-docs-notes-card" className="bg-amber-50 rounded-xl border border-amber-300 p-4">
              <Label className="text-xs text-amber-900 font-semibold uppercase tracking-wide">Additional Documents / Details Required — Notes</Label>
              <Textarea
                data-testid="additional-docs-notes"
                defaultValue={a.additional_docs_notes || ""}
                placeholder="List the exact documents or details the student still needs to provide…"
                onBlur={e => e.target.value !== (a.additional_docs_notes || "") && patch({ additional_docs_notes: e.target.value })}
                className="mt-2 min-h-24 bg-white"
              />
            </div>
          )}
        </TabsContent>

        <TabsContent value="admissions" className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="College">
              {a.college_id ? <LockedCollege a={a} testid="edit-college" /> : (
              <Select value={a.college_id||""} onValueChange={v=>patch({college_id: v})}>
                <SelectTrigger data-testid="edit-college"><SelectValue placeholder="Select college" /></SelectTrigger>
                <SelectContent>
                  {(meta.colleges||[]).map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.code ? `${c.code} — ${c.name}` : c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>)}
            </Field>
            <CategoryCourseFields a={a} patch={patch} prefix="edit" />
            <Field label="Intake">
              <Select value={a.intake_id||""} onValueChange={v=>patch({intake_id: v})}>
                <SelectTrigger data-testid="edit-intake"><SelectValue placeholder="Select intake" /></SelectTrigger>
                <SelectContent>
                  {upcomingIntakes(meta.intakes, a.intake_id).map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="College App ID"><Input defaultValue={a.college_app_id||""} onBlur={e=>e.target.value!==a.college_app_id && patch({college_app_id:e.target.value})}/></Field>
            <Field label="Documents Folder Link"><Input defaultValue={a.documents_folder_link||""} onBlur={e=>e.target.value!==a.documents_folder_link && patch({documents_folder_link:e.target.value})}/></Field>
            <Field label="Documents Status">
              <Select value={a.documents_status} onValueChange={v=>patch({documents_status:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Assigned BDM">
              <Select value={a.assigned_bdm_id||""} onValueChange={v=>patch({assigned_bdm_id:v})}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>{meta.users.filter(u=>["bdm","admin"].includes(u.role)).map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Assigned Admissions Officer">
              <Select value={a.assigned_admissions_officer_id||""} onValueChange={v=>patch({assigned_admissions_officer_id:v})}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>{meta.users.filter(u=>["admissions_officer","admin"].includes(u.role)).map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Missing Documents">
              <MissingDocsPicker
                value={(a.missing_documents_list && a.missing_documents_list.length) ? a.missing_documents_list
                  : (a.missing_documents ? a.missing_documents.split(",").map(s=>s.trim()).filter(Boolean) : [])}
                onChange={v => patch({ missing_documents_list: v, missing_documents: v.join(", ") })}
                allowCatalogEdit={["admin","management","bdm","admissions_officer","manager"].includes(user?.role)}
              />
            </Field>
            <Field label="Date of Birth"><Input data-testid="edit-dob" type="date" defaultValue={a.date_of_birth||""} onBlur={e=>e.target.value!==(a.date_of_birth||"") && patch({date_of_birth:e.target.value})}/></Field>
            <Field label="Study Permit Deadline"><Input data-testid="edit-study-permit-deadline" type="date" defaultValue={a.study_permit_deadline||""} onBlur={e=>e.target.value!==(a.study_permit_deadline||"") && patch({study_permit_deadline:e.target.value})}/></Field>
            <Field label="Student Location">
              <Select value={a.student_location||""} onValueChange={v=>patch({student_location:v})}>
                <SelectTrigger data-testid="edit-student-location"><SelectValue placeholder="Choose"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Onshore">Onshore</SelectItem>
                  <SelectItem value="Offshore">Offshore</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Payment Deadline"><Input data-testid="edit-payment-deadline" type="date" defaultValue={a.payment_deadline||""} onBlur={e=>e.target.value!==(a.payment_deadline||"") && patch({payment_deadline:e.target.value})}/></Field>
            <Field label="College Student ID"><Input data-testid="edit-college-student-id" defaultValue={a.college_student_id||""} placeholder="e.g. N12345678" onBlur={e=>e.target.value!==(a.college_student_id||"") && patch({college_student_id:e.target.value})}/></Field>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs text-slate-500 mb-2">Click a category to pick a file and upload it. Use “General Upload” for anything that doesn’t match a category.</div>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" data-testid="doc-file-input" className="hidden" />
              <Button data-testid="upload-general" onClick={()=>pickAndUpload("document")} disabled={uploading}><Upload className="w-4 h-4 mr-1"/>General Upload</Button>
              <Button data-testid="upload-offer-letter" variant="outline" onClick={()=>pickAndUpload("offer_letter")} disabled={uploading}>Offer Letter</Button>
              <Button data-testid="upload-fee-receipt" variant="outline" onClick={()=>pickAndUpload("fee_receipt")} disabled={uploading}>Fee Receipt</Button>
              <Button data-testid="upload-pal" variant="outline" onClick={()=>pickAndUpload("pal")} disabled={uploading}>PAL</Button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="text-left px-4 py-3">Filename</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Uploaded</th><th></th></tr>
              </thead>
              <tbody>
                {data.documents.map(d => (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" /> {d.original_filename}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{d.doc_type?.replace(/_/g," ")}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(d.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={()=>downloadFile(d)}><Download className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={async()=>{ await api.delete(`/files/${d.id}`); load(); }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
                {data.documents.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-500">No documents uploaded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="enrollment" className="space-y-4">
          <B2BEnrollmentTab appId={id} app={a} semesters={data.semesters||[]} reload={load} />
        </TabsContent>

        {canFinance && <TabsContent value="commissions" className="space-y-4">
          <B2BCommissionTab claims={data.claims||[]} reload={load} />
        </TabsContent>}

        <TabsContent value="tasks" className="space-y-4">
          <TasksBlock appId={id} tasks={data.tasks} users={meta.users} reload={load} />
        </TabsContent>

        <TabsContent value="activity">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <ol className="relative border-l border-slate-200 space-y-4">
              {data.activity.map(ev => (
                <li key={ev.id} className="ml-4">
                  <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-orange-500" />
                  <div className="text-xs text-slate-500">{new Date(ev.created_at).toLocaleString()}</div>
                  <div className="text-sm text-slate-900"><UserCircle2 className="inline w-4 h-4 text-slate-400 mr-1" /><b>{ev.actor_name}</b> · <span className="text-slate-600">{ev.action.replace(/_/g," ")}</span></div>
                  {ev.details && <div className="text-xs text-slate-600 mt-0.5">{ev.details}</div>}
                </li>
              ))}
              {data.activity.length === 0 && <div className="text-slate-500 text-sm">No activity yet.</div>}
            </ol>
          </div>
        </TabsContent>
      </Tabs>
      <AppNotesPanel appId={id} currentUserId={user?.user_id} />
      </div>

      <StageReasonModal stage={reasonStage} onCancel={() => setReasonStage(null)}
        onConfirm={async (reason) => { await patch({ stage: reasonStage, stage_reason: reason }); setReasonStage(null); }} />
      <SiblingDecisionModal open={!!siblingAsk} siblings={openSiblings}
        onCancel={() => { siblingAsk?.resolve(null); setSiblingAsk(null); }}
        onChoose={(v) => { siblingAsk?.resolve({ sibling_action: v }); setSiblingAsk(null); }} />

      <FilingStatusModal
        open={filingModal.open}
        onOpenChange={(v)=>setFilingModal(m=>({...m, open: v}))}
        applicationId={id}
        newStatus={filingModal.status}
        onDone={load}
      />
    </div>
  );
}


const LockedCollege = ({ a, testid }) => (
  <div data-testid={testid} aria-disabled="true" title="College is locked once the application is created"
    className="flex items-center gap-2 h-8 px-3 rounded-md border border-slate-200 bg-slate-50 text-[13px] text-slate-700 select-none cursor-not-allowed">
    <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
    <span className="truncate">{a.college_code ? `${a.college_code} — ${a.college}` : a.college}</span>
  </div>
);

const Field = ({ label, children }) => (<div className="space-y-1"><Label className="text-[11px] text-slate-600">{label}</Label>{children}</div>);
function B2BEnrollmentTab({ appId, app, semesters, reload }) {
  const addNext = async () => {
    try { await api.post(`/applications/${appId}/semesters`, {}); toast.success("Semester added"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const canAdd = app.decision === "Approved";
  return (
    <div className="space-y-4">
      {canAdd && (
        <Button data-testid="add-next-semester-btn" onClick={addNext} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="w-4 h-4 mr-2"/>Add Next Semester
        </Button>
      )}
      {semesters.length === 0 && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">
          Semester 1 is auto-created when the Filing Status is marked <span className="font-semibold">Approved</span>.
        </div>
      )}
      {semesters.map(s => <B2BSemesterCard key={s.id} sem={s} reload={reload} />)}
    </div>
  );
}

const B2B_SEM_STATUSES = ["PENDING","ENROLLED","DEFERRED","WITHDRAWN"];
const semBadge = (s) => s === "ENROLLED" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
  : s === "DEFERRED" ? "bg-amber-50 text-amber-800 border-amber-300"
  : s === "WITHDRAWN" ? "bg-rose-50 text-rose-800 border-rose-300"
  : "bg-slate-50 text-slate-700 border-slate-300";

function B2BSemesterCard({ sem, reload }) {
  const change = async (status) => {
    try {
      const body = { status };
      if (status === "WITHDRAWN") {
        const reason = window.prompt("Withdrawal reason?"); if (!reason) return;
        body.withdrawal_reason = reason;
      }
      await api.patch(`/semesters/${sem.id}`, body);
      toast.success(`Semester ${sem.semester_number} → ${status}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid={`b2b-semester-${sem.semester_number}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="heading-font font-bold text-slate-900">Semester {sem.semester_number}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          Original: {sem.original_intake_name || "—"} · Current: {sem.current_intake_name || "—"}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`stage-badge border ${semBadge(sem.status)}`}>{sem.status}</span>
        <Select value={sem.status} onValueChange={change}>
          <SelectTrigger data-testid={`b2b-semester-status-${sem.semester_number}`} className="h-9 w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>{B2B_SEM_STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

const B2B_CLAIM_STATUSES = ["INVOICE PENDING","INVOICE SENT","COMMISSION RECEIVED"];
const claimBadge = (s) => s === "COMMISSION RECEIVED" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
  : s === "INVOICE SENT" ? "bg-sky-50 text-sky-800 border-sky-300"
  : "bg-slate-50 text-slate-700 border-slate-300";

function B2BCommissionTab({ claims, reload }) {
  if (claims.length === 0) {
    return <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">
      Commission claims are auto-created when a semester is marked <span className="font-semibold">ENROLLED</span>.
    </div>;
  }
  return <div className="space-y-4">{claims.map(c => <B2BClaimCard key={c.id} c={c} reload={reload} />)}</div>;
}

function B2BClaimCard({ c, reload }) {
  const patch = async (body) => {
    try { await api.patch(`/commission-claims/${c.id}`, body); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div data-testid={`b2b-claim-${c.semester_number}`} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="heading-font font-bold text-slate-900">Semester {c.semester_number} Claim</div>
          <div className="text-xs text-slate-500 mt-0.5">Invoice: {c.invoice_number || "—"}</div>
        </div>
        <div className="text-right flex items-start gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Expected</div>
            <div className="heading-font text-sm font-semibold">{c.expected_commission_amount != null ? CAD(c.expected_commission_amount) : "—"}</div>
          </div>
          <span className={`stage-badge border ${claimBadge(c.status)}`}>{c.status}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Expected (CAD)">
          <Input type="number" defaultValue={c.expected_commission_amount ?? ""}
            onBlur={e=>{ const v = e.target.value===""?null:Number(e.target.value); if (v !== c.expected_commission_amount) patch({expected_commission_amount: v}); }}/>
        </Field>
        <Field label="Invoice #">
          <Input defaultValue={c.invoice_number||""} onBlur={e=>e.target.value!==(c.invoice_number||"") && patch({invoice_number: e.target.value})}/>
        </Field>
        <Field label="Status">
          <Select value={c.status} onValueChange={v=>patch({status:v})}>
            <SelectTrigger data-testid={`b2b-claim-status-${c.semester_number}`}><SelectValue /></SelectTrigger>
            <SelectContent>{B2B_CLAIM_STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        {c.status === "COMMISSION RECEIVED" && (
          <>
            <Field label="Received Date">
              <Input type="date" defaultValue={c.commission_received_date||""} onBlur={e=>e.target.value!==(c.commission_received_date||"") && patch({commission_received_date: e.target.value})}/>
            </Field>
            <Field label="Amount Received (CAD)">
              <Input type="number" defaultValue={c.amount_received ?? ""}
                onBlur={e=>{ const v = e.target.value===""?null:Number(e.target.value); if (v !== c.amount_received) patch({amount_received: v}); }}/>
            </Field>
          </>
        )}
      </div>
    </div>
  );
}



function EnrollmentBlock({ en, reload }) {
  const patch = async (body) => { await api.patch(`/enrollments/${en.id}`, body); reload(); };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Status">
        <Select value={en.status} onValueChange={v=>patch({status:v})}>
          <SelectTrigger data-testid="enr-status"><SelectValue /></SelectTrigger>
          <SelectContent>{ENROLL_STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Current Semester"><Input defaultValue={en.current_semester||""} onBlur={e=>patch({current_semester:e.target.value})}/></Field>
      <Field label="Semester 1 Start"><Input type="date" defaultValue={en.semester1_start||""} onBlur={e=>patch({semester1_start:e.target.value})}/></Field>
      <Field label="Semester 1 Confirmed">
        <Select value={String(en.semester1_confirmed)} onValueChange={v=>patch({semester1_confirmed: v==="true"})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
        </Select>
      </Field>
      <Field label="Semester 2 Start"><Input type="date" defaultValue={en.semester2_start||""} onBlur={e=>patch({semester2_start:e.target.value})}/></Field>
      <Field label="Semester 2 Confirmed">
        <Select value={String(en.semester2_confirmed)} onValueChange={v=>patch({semester2_confirmed: v==="true"})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
        </Select>
      </Field>
      <Field label="Next Follow-up"><Input type="date" defaultValue={en.next_followup_date||""} onBlur={e=>patch({next_followup_date:e.target.value})}/></Field>
      <div className="md:col-span-2"><Field label="Notes"><Textarea defaultValue={en.notes||""} onBlur={e=>patch({notes:e.target.value})}/></Field></div>
    </div>
  );
}

function CommissionBlock({ c, reload }) {
  const patch = async (body) => { await api.patch(`/commissions/${c.id}`, body); reload(); };
  const net = (c.commission_amount_received||0) - (c.partner_amount_paid||0);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="heading-font font-semibold text-slate-900">{c.semester_period}</div>
          <div className="text-xs text-slate-500 mono">{c.invoice_number || "No invoice #"}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Company Net</div>
          <div className="heading-font text-xl font-bold text-slate-900">{CAD(net)}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Semester / Period"><Input defaultValue={c.semester_period} onBlur={e=>patch({semester_period:e.target.value})}/></Field>
        <Field label="Expected Commission (CAD)"><Input type="number" defaultValue={c.expected_commission} onBlur={e=>patch({expected_commission:Number(e.target.value)})}/></Field>
        <Field label="Status">
          <Select value={c.status} onValueChange={v=>patch({status:v})}>
            <SelectTrigger data-testid="comm-status"><SelectValue /></SelectTrigger>
            <SelectContent>{COMMISSION_STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Invoice Number"><Input defaultValue={c.invoice_number||""} onBlur={e=>patch({invoice_number:e.target.value})}/></Field>
        <Field label="Invoice Status"><Input defaultValue={c.invoice_status||""} onBlur={e=>patch({invoice_status:e.target.value})}/></Field>
        <Field label="Invoice Submitted"><Input type="date" defaultValue={c.invoice_submitted_date||""} onBlur={e=>patch({invoice_submitted_date:e.target.value})}/></Field>
        <Field label="Expected Payment"><Input type="date" defaultValue={c.expected_payment_date||""} onBlur={e=>patch({expected_payment_date:e.target.value})}/></Field>
        <Field label="Commission Received Date"><Input type="date" defaultValue={c.commission_received_date||""} onBlur={e=>patch({commission_received_date:e.target.value})}/></Field>
        <Field label="Commission Received (CAD)"><Input type="number" defaultValue={c.commission_amount_received} onBlur={e=>patch({commission_amount_received:Number(e.target.value)})}/></Field>
        <Field label="Partner Payable (CAD)"><Input type="number" defaultValue={c.partner_commission_payable} onBlur={e=>patch({partner_commission_payable:Number(e.target.value)})}/></Field>
        <Field label="Partner Paid (CAD)"><Input type="number" defaultValue={c.partner_amount_paid} onBlur={e=>patch({partner_amount_paid:Number(e.target.value)})}/></Field>
        <Field label="Partner Payment Status">
          <Select value={c.partner_payment_status} onValueChange={v=>patch({partner_payment_status:v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["Not Paid","Partially Paid","Paid"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}

function TasksBlock({ appId, tasks, users, reload }) {
  const [t, setT] = useState({ title: "", due_date: "", priority: "Medium" });
  const add = async () => {
    if (!t.title || !t.due_date) { toast.error("Title and due date required"); return; }
    await api.post("/tasks", { ...t, application_id: appId });
    setT({ title: "", due_date: "", priority: "Medium" }); reload();
  };
  return (
    <div className="space-y-4">
      <div className="bg-white p-4 rounded-xl border border-slate-200 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-48"><Field label="Task"><Input data-testid="task-title-input" value={t.title} onChange={e=>setT({...t, title:e.target.value})}/></Field></div>
        <Field label="Due Date"><Input type="date" data-testid="task-due-input" value={t.due_date} onChange={e=>setT({...t, due_date:e.target.value})}/></Field>
        <Field label="Priority">
          <Select value={t.priority} onValueChange={v=>setT({...t, priority:v})}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Assign To">
          <Select value={t.assigned_to_id||""} onValueChange={v=>setT({...t, assigned_to_id:v})}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>{users.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Button data-testid="task-add-btn" onClick={add}><Plus className="w-4 h-4 mr-1"/> Add</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="text-left px-4 py-3">Task</th><th className="text-left px-4 py-3">Due</th><th className="text-left px-4 py-3">Priority</th><th className="text-left px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {tasks.map(t => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{t.title}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(t.due_date)}</td>
                <td className="px-4 py-3"><span className={`stage-badge ${priorityClass(t.priority)}`}>{t.priority}</span></td>
                <td className="px-4 py-3">
                  <Select value={t.status} onValueChange={async v=>{ await api.patch(`/tasks/${t.id}`, { status: v }); reload(); }}>
                    <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Open","In Progress","Done","Cancelled"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {tasks.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No tasks yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SIBLING_ACCEPTED = ["Fees Paid", "PAL Pending", "PAL Issued", "Admission Complete"];
const SIBLING_CLOSED = ["Application Rejected", "Withdrawn", "Closed", "Backed Out", "Selected Another College", "Deferred", ...SIBLING_ACCEPTED];

function SiblingOffersChip({ siblings }) {
  if (!siblings.length) return null;
  const open = siblings.filter(s => !SIBLING_CLOSED.includes(s.stage) && !s.deposit_paid);
  const accepted = siblings.find(s => SIBLING_ACCEPTED.includes(s.stage) || s.deposit_paid);
  const tone = open.length ? "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100" : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="sibling-offers-chip" className={`stage-badge ${tone} cursor-pointer`}>
          <Layers className="w-3 h-3" /> {siblings.length} other offer{siblings.length > 1 ? "s" : ""}
          {open.length > 0 && <span className="font-bold">· {open.length} open</span>}
          {accepted && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
          <ChevronDown className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" data-testid="sibling-offers-popover" className="w-80 p-2 bg-white">
        <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-500">Other offers for this student</div>
        {accepted && <div data-testid="sibling-accepted" className="px-2 pb-1 text-xs text-emerald-700">Accepted: {accepted.college_code || accepted.college} ({accepted.application_id})</div>}
        <div className="max-h-64 overflow-y-auto">
          {siblings.map(s => (
            <Link key={s.id} data-testid={`sibling-offer-${s.id}`} to={`/applications/${s.id}`}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-orange-50 min-w-0">
              <span className="mono text-slate-500 shrink-0">{s.application_id}</span>
              <span className="font-medium text-slate-800 truncate flex-1" title={s.program}>{s.college_code || s.college}</span>
              <span className={`stage-badge shrink-0 ${stageClass(s.stage)}`}>{s.stage}</span>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

